import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { PageSeo } from '@/components/seo/PageSeo';
import {
  MessageSquare,
  Globe,
  Smartphone,
  Bell,
  MapPin,
  Calendar,
  Users,
  Wifi,
  Car,
  Sparkles,
  Shield,
  QrCode,
  Languages,
  Zap,
  CreditCard,
  FileText,
  RefreshCw,
  ArrowRight,
  Check,
  Star,
  Bot,
  Camera,
  TrendingUp,
  Clock,
} from 'lucide-react';
import { Button, LanguageModal } from '@/components/ui';

const content = {
  hr: {
    meta: {
      title: 'Funkcionalnosti | BookiApp - Aplikacija za goste smještaja',
      description: 'Sve funkcionalnosti BookiApp-a: chat s prijevodom na 95+ jezika, PWA aplikacija za goste, push obavijesti, AI asistent, kalendar sinkronizacija i više.',
    },
    hero: {
      badge: 'FUNKCIONALNOSTI',
      title: 'Sve što trebate za',
      titleHighlight: 'profesionalno gostoprimstvo',
      subtitle: 'BookiApp objedinjuje komunikaciju s gostima, upravljanje rezervacijama i digitalni vodič u jednu jednostavnu platformu.',
    },
    categories: [
      {
        id: 'guest-portal',
        badge: 'APP ZA GOSTE',
        title: 'Personalizirana aplikacija za goste',
        subtitle: 'Svaki gost dobiva vlastitu aplikaciju s informacijama o boravku',
        features: [
          { icon: 'smartphone', title: 'PWA aplikacija', desc: 'Gost otvara link u pregledniku - bez instalacije iz App Store-a. Opcionalno dodaje na početni ekran kao pravu aplikaciju.' },
          { icon: 'wifi', title: 'WiFi, upute, pravila', desc: 'Sve informacije o smještaju na jednom mjestu. WiFi lozinka s copy gumbom, upute za dolazak, kućna pravila.' },
          { icon: 'qr', title: 'QR kod pristup', desc: 'Generirajte QR kod i pošaljite gostu. Jedan sken i gost ima sve informacije.' },
          { icon: 'globe', title: 'Automatski prijevod sadržaja', desc: 'Sav sadržaj se automatski prevodi na jezik gosta. Vi pišete na hrvatskom, gost čita na svom jeziku.' },
          { icon: 'shield', title: 'Offline pristup', desc: 'PWA radi i bez interneta. Jednom otvoreno, informacije su dostupne i offline.' },
          { icon: 'star', title: 'Dark/Light mode', desc: 'Gost bira temu koja mu odgovara. Automatski se prilagođava postavkama uređaja.' },
        ],
      },
      {
        id: 'chat',
        badge: 'CHAT S PRIJEVODOM',
        title: 'Komunikacija bez jezičnih barijera',
        subtitle: 'Dvosmjerni chat s automatskim prijevodom na 95+ jezika',
        features: [
          { icon: 'messages', title: 'Real-time chat', desc: 'Instant poruke između vas i gosta. Podržava tekst, slike i emojije.' },
          { icon: 'languages', title: '95+ jezika', desc: 'Automatski prijevod u oba smjera. Vi pišete hrvatski, gost čita talijanski, njemački ili bilo koji drugi jezik.' },
          { icon: 'bot', title: 'AI asistent za goste', desc: 'Kada vi niste dostupni, AI odgovara na česta pitanja gostiju koristeći podatke iz vašeg smještaja.' },
          { icon: 'bell', title: 'Push obavijesti', desc: 'Primite obavijest kad gost pošalje poruku, čak i kad aplikacija nije otvorena.' },
          { icon: 'camera', title: 'Slanje slika', desc: 'Gost može poslati sliku problema, vi možete poslati sliku uputa.' },
          { icon: 'check', title: 'Status poruka', desc: 'Vidite kad je poruka isporučena i pročitana. Typing indikator kad druga strana piše.' },
        ],
      },
      {
        id: 'booking-management',
        badge: 'UPRAVLJANJE REZERVACIJAMA',
        title: 'Sve rezervacije na jednom mjestu',
        subtitle: 'Kalendar, sinkronizacija, AI skeniranje i praćenje uplata',
        features: [
          { icon: 'calendar', title: 'Multi-unit kalendar', desc: 'Pregledajte zauzetost svih jedinica na jednom ekranu. Nikad više dupla rezervacija.' },
          { icon: 'sync', title: 'iCal sinkronizacija', desc: 'Automatski sync s Booking.com i Airbnb svakih 15 minuta. Embed kalendar za vlastitu web stranicu.' },
          { icon: 'sparkles', title: 'AI skeniranje rezervacija', desc: 'Slikajte potvrdu s Booking.com ili Airbnb - AI automatski popuni sve podatke rezervacije.' },
          { icon: 'trending', title: 'Pametne preporuke', desc: 'Kad su datumi zauzeti, sustav predlaže alternativne jedinice ili pomak termina.' },
          { icon: 'creditcard', title: 'Praćenje uplata', desc: 'Evidentirajte uplate, generirajte račune, gost vidi status plaćanja u svojoj aplikaciji.' },
          { icon: 'users', title: 'Grupne rezervacije', desc: 'Jedna obitelj, više apartmana? Jedan link za sve jedinice. Gost lako prebacuje između njih.' },
        ],
      },
      {
        id: 'guest-experience',
        badge: 'ISKUSTVO GOSTA',
        title: 'Premium iskustvo bez premium budžeta',
        subtitle: 'Značajke koje gostu daju osjećaj da boravi u 5-zvjezdičnom hotelu',
        features: [
          { icon: 'map', title: 'Lokalne preporuke', desc: 'Podijelite omiljene restorane, plaže, atrakcije. AI pronalazi obližnja mjesta i dodaje ih automatski.' },
          { icon: 'car', title: 'Parking navigacija', desc: 'GPS koordinate parkinga s navigacijom. Gost otvori Google Maps ili Waze jednim klikom.' },
          { icon: 'clock', title: 'Check-in/out upute', desc: 'Detaljne upute za dolazak i odlazak. Self check-in s kodovima za lockbox ili keypad.' },
          { icon: 'zap', title: 'Quick Actions', desc: 'Gost jednim klikom javlja "Krenuo sam", "Na parkingu sam", "Stigao sam". Vi dobijete push obavijest.' },
          { icon: 'file', title: 'Dokumenti za goste', desc: 'Gost može preuzeti račun, potvrdu rezervacije i druga dokumenta direktno iz aplikacije.' },
          { icon: 'bot', title: 'AI domaćin asistent', desc: 'AI asistent za vas kao iznajmljivača - pomaže s onboardingom, odgovara na pitanja o korištenju aplikacije.' },
        ],
      },
    ],
    cta: {
      title: 'Spremni isprobati?',
      subtitle: 'Pokrenite svoju aplikaciju za goste u 5 minuta.',
      button: 'Započni sada',
      demo: 'Pogledajte što gost vidi',
    },
  },
  en: {
    meta: {
      title: 'Features | BookiApp - Guest Experience Portal',
      description: 'All BookiApp features: chat with translation to 95+ languages, PWA guest app, push notifications, AI assistant, calendar sync and more.',
    },
    hero: {
      badge: 'FEATURES',
      title: 'Everything you need for',
      titleHighlight: 'professional hospitality',
      subtitle: 'BookiApp combines guest communication, booking management, and a digital guide into one simple platform.',
    },
    categories: [
      {
        id: 'guest-portal',
        badge: 'GUEST APP',
        title: 'Personalized app for every guest',
        subtitle: 'Each guest gets their own app with stay information',
        features: [
          { icon: 'smartphone', title: 'PWA app', desc: 'Guest opens a link in their browser - no App Store install needed. Optionally adds to home screen as a real app.' },
          { icon: 'wifi', title: 'WiFi, directions, rules', desc: 'All accommodation info in one place. WiFi password with copy button, arrival directions, house rules.' },
          { icon: 'qr', title: 'QR code access', desc: 'Generate a QR code and send to guest. One scan and the guest has everything.' },
          { icon: 'globe', title: 'Auto content translation', desc: 'All content is automatically translated to the guest\'s language. You write in your language, guest reads in theirs.' },
          { icon: 'shield', title: 'Offline access', desc: 'PWA works without internet. Once opened, information is available offline.' },
          { icon: 'star', title: 'Dark/Light mode', desc: 'Guest chooses their preferred theme. Automatically adapts to device settings.' },
        ],
      },
      {
        id: 'chat',
        badge: 'TRANSLATED CHAT',
        title: 'Communication without language barriers',
        subtitle: 'Two-way chat with automatic translation to 95+ languages',
        features: [
          { icon: 'messages', title: 'Real-time chat', desc: 'Instant messages between you and guest. Supports text, images, and emojis.' },
          { icon: 'languages', title: '95+ languages', desc: 'Automatic translation both ways. You write Croatian, guest reads Italian, German, or any other language.' },
          { icon: 'bot', title: 'AI guest assistant', desc: 'When you\'re unavailable, AI answers common guest questions using your property data.' },
          { icon: 'bell', title: 'Push notifications', desc: 'Get notified when guest sends a message, even when the app isn\'t open.' },
          { icon: 'camera', title: 'Image sharing', desc: 'Guest can send a photo of an issue, you can send a photo of instructions.' },
          { icon: 'check', title: 'Message status', desc: 'See when message is delivered and read. Typing indicator when the other side is typing.' },
        ],
      },
      {
        id: 'booking-management',
        badge: 'BOOKING MANAGEMENT',
        title: 'All bookings in one place',
        subtitle: 'Calendar, sync, AI scanning, and payment tracking',
        features: [
          { icon: 'calendar', title: 'Multi-unit calendar', desc: 'View availability of all units on one screen. Never double-book again.' },
          { icon: 'sync', title: 'iCal synchronization', desc: 'Auto-sync with Booking.com and Airbnb every 15 minutes. Embed calendar on your website.' },
          { icon: 'sparkles', title: 'AI booking scanner', desc: 'Take a photo of your Booking.com or Airbnb confirmation - AI fills in all booking details.' },
          { icon: 'trending', title: 'Smart suggestions', desc: 'When dates are taken, the system suggests alternative units or date shifts.' },
          { icon: 'creditcard', title: 'Payment tracking', desc: 'Record payments, generate invoices, guest sees payment status in their app.' },
          { icon: 'users', title: 'Group bookings', desc: 'One family, multiple apartments? One link for all units. Guest easily switches between them.' },
        ],
      },
      {
        id: 'guest-experience',
        badge: 'GUEST EXPERIENCE',
        title: 'Premium experience without premium budget',
        subtitle: 'Features that make guests feel like they\'re staying at a 5-star hotel',
        features: [
          { icon: 'map', title: 'Local recommendations', desc: 'Share favorite restaurants, beaches, attractions. AI finds nearby places and adds them automatically.' },
          { icon: 'car', title: 'Parking navigation', desc: 'GPS coordinates with navigation. Guest opens Google Maps or Waze with one click.' },
          { icon: 'clock', title: 'Check-in/out instructions', desc: 'Detailed arrival and departure instructions. Self check-in with lockbox or keypad codes.' },
          { icon: 'zap', title: 'Quick Actions', desc: 'Guest taps "I\'m on my way", "At parking", "I arrived" with one click. You get a push notification.' },
          { icon: 'file', title: 'Guest documents', desc: 'Guest can download invoices, booking confirmations, and other documents directly from the app.' },
          { icon: 'bot', title: 'AI host assistant', desc: 'AI assistant for you as a host - helps with onboarding, answers questions about using the app.' },
        ],
      },
    ],
    cta: {
      title: 'Ready to try?',
      subtitle: 'Launch your Guest Experience Portal in 5 minutes.',
      button: 'Get started',
      demo: 'See what guests see',
    },
  },
};

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  smartphone: Smartphone,
  messages: MessageSquare,
  bell: Bell,
  map: MapPin,
  calendar: Calendar,
  users: Users,
  wifi: Wifi,
  car: Car,
  sparkles: Sparkles,
  shield: Shield,
  qr: QrCode,
  languages: Languages,
  zap: Zap,
  creditcard: CreditCard,
  file: FileText,
  sync: RefreshCw,
  globe: Globe,
  star: Star,
  bot: Bot,
  camera: Camera,
  trending: TrendingUp,
  clock: Clock,
  check: Check,
};

const categoryGradients = [
  'from-cyan-500 to-blue-500',
  'from-blue-500 to-indigo-500',
  'from-emerald-500 to-teal-500',
  'from-amber-500 to-orange-500',
];

const categoryBg = [
  'from-cyan-500/5 to-blue-500/5',
  'from-blue-500/5 to-indigo-500/5',
  'from-emerald-500/5 to-teal-500/5',
  'from-amber-500/5 to-orange-500/5',
];

export default function FeaturesPage() {
  const { i18n } = useTranslation();
  const lang = (i18n.language === 'hr' ? 'hr' : 'en') as 'hr' | 'en';
  const t = content[lang];
  const [langOpen, setLangOpen] = useState(false);

  return (
    <>
      <PageSeo path="/features" title={t.meta.title} description={t.meta.description} />

      <div className="min-h-screen bg-[hsl(var(--background))]">
        {/* Header */}
        <header className="sticky top-0 z-50 bg-[hsl(var(--background))]/80 backdrop-blur-xl border-b border-[hsl(var(--border))]/50">
          <div className="container mx-auto flex items-center justify-between px-4 py-3">
            <Link to="/" className="flex items-center">
              <img src="/favicon-original.png" alt="BookiApp" className="h-8 w-auto" />
            </Link>
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setLangOpen(true)}
                className="px-2"
                aria-haspopup="dialog"
                aria-expanded={langOpen}
              >
                <Globe className="h-4 w-4 mr-1" />
                {lang.toUpperCase()}
              </Button>
              <Link to="/pricing">
                <Button size="sm" className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white border-0">
                  {t.cta.button}
                </Button>
              </Link>
            </div>
          </div>
        </header>

        {/* Hero */}
        <section className="pt-16 pb-12 md:pt-24 md:pb-16 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-[40%] -left-[20%] w-[80%] h-[80%] bg-gradient-to-br from-cyan-400/20 via-blue-400/10 to-transparent rounded-full blur-[100px]" />
            <div className="absolute -bottom-[30%] -right-[20%] w-[60%] h-[60%] bg-gradient-to-tl from-blue-500/15 to-transparent rounded-full blur-[100px]" />
          </div>
          <div className="container mx-auto px-4 relative text-center">
            <span className="inline-block px-3 py-1 rounded-full bg-cyan-500/10 text-cyan-600 text-xs font-medium mb-6">{t.hero.badge}</span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight mb-6 leading-[1.1]">
              {t.hero.title}{' '}
              <span className="bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent">{t.hero.titleHighlight}</span>
            </h1>
            <p className="text-lg md:text-xl text-[hsl(var(--muted-foreground))] max-w-3xl mx-auto leading-relaxed">{t.hero.subtitle}</p>
          </div>
        </section>

        {/* Feature Categories */}
        {t.categories.map((cat, catIdx) => (
          <section key={cat.id} id={cat.id} className={`py-16 md:py-24 ${catIdx % 2 === 1 ? 'bg-[hsl(var(--muted))]/30' : ''}`}>
            <div className="container mx-auto px-4">
              <div className="text-center mb-12 md:mb-16">
                <span className={`inline-block px-3 py-1 rounded-full bg-gradient-to-r ${categoryBg[catIdx]} text-xs font-medium mb-4`}>
                  <span className={`bg-gradient-to-r ${categoryGradients[catIdx]} bg-clip-text text-transparent font-semibold`}>{cat.badge}</span>
                </span>
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">{cat.title}</h2>
                <p className="text-lg text-[hsl(var(--muted-foreground))] max-w-2xl mx-auto">{cat.subtitle}</p>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                {cat.features.map((feat, i) => {
                  const Icon = iconMap[feat.icon] || Sparkles;
                  return (
                    <div key={i} className="group bg-[hsl(var(--card))] rounded-2xl border border-[hsl(var(--border))]/50 p-6 hover:border-cyan-500/20 hover:shadow-lg hover:shadow-cyan-500/5 transition-all duration-300">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${categoryGradients[catIdx]} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                      <h3 className="font-bold text-lg mb-2">{feat.title}</h3>
                      <p className="text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">{feat.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        ))}

        {/* CTA */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto relative rounded-3xl overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500 via-blue-600 to-indigo-600" />
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,255,255,0.15),transparent_50%)]" />
              <div className="relative px-8 py-14 md:px-16 md:py-20 text-center text-white">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-4">{t.cta.title}</h2>
                <p className="text-lg md:text-xl opacity-90 mb-8 max-w-xl mx-auto">{t.cta.subtitle}</p>
                <div className="flex flex-col sm:flex-row justify-center gap-4">
                  <Link to="/pricing">
                    <Button size="lg" className="bg-white text-blue-600 hover:bg-white/90 shadow-lg text-base px-8 h-12 rounded-xl group font-bold">
                      {t.cta.button}
                      <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                  <Link to="/demo">
                    <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 text-base px-8 h-12 rounded-xl">
                      {t.cta.demo}
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer link */}
        <div className="text-center pb-12">
          <Link to="/" className="text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors">
            &larr; {lang === 'hr' ? 'Povratak na početnu' : 'Back to home'}
          </Link>
        </div>
      </div>

      <LanguageModal open={langOpen} onClose={() => setLangOpen(false)} scope="marketing" />
    </>
  );
}
